import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Link from 'components/common/link';
import {
  // isIEBrowser,
  // isEdgeBrowser,
  formatMarkdownToHtml,
} from 'utils';
import { TextBalancer } from 'components/common';
import {
  ProductVariantPicker,
  utils as productVariantsUtils,
} from 'lib/productVariants';
// import { AddToCart, CartLineQuantityInput } from 'lib/cart';
import {
  ProductVariantAddonSelectGroup,
  getNextVisibleSelectedAddons,
  useProductVariantAddonOptions,
  useProductVariantAddonRules,
  shallowEqual,
} from 'lib/productAddon';
import { isEmpty } from 'lib/javascript';
import * as typedef from 'lib/product/typedef';
import * as analytics from 'lib/analytics';
import { routePaths } from 'appConfig';

import ProductPrice from '../ProductPrice/ProductPrice';
import useProductCustomization from '../useProductCustomization';
import style from './ProductDetail.module.scss';
import { SeeMenu } from 'lib/menu';
import ProductShare from '../ProductShare';
import { isCustomizableMeal } from 'lib/product';

const Links = ({ label, data }) => {
  if (isEmpty(data)) return null;

  return (
    <div className="product-cat">
      <span>{label}: </span>
      {data.map((link, index) => (
        <span key={index} className="mr-0">
          <Link to={link.to}>{link.label}</Link>
          {index < data.length - 1 ? ', ' : ''}
        </span>
      ))}
    </div>
  );
};

const ProductDetail = ({
  product,
  catalogueIds,
  children,
  onFirstItemAdded,
  categories,
  collections,
  specialProductCategories,
}) => {
  const {
    selectedAddons,
    selectedAddonOptionValueIds,
    selectedVariant,
    // quantity,
    setSelectedAddons,
    setSelectedVariant,
    // setQuantity,
    // isAnyAddonOutOfStock,
  } = useProductCustomization({ syncWithQuery: true });

  // eslint-disable-next-line no-unused-vars
  const [_isAddonOptionsValid, setIsAddonOptionsValid] = useState(
    true,
  );

  const resetMeal = useCallback(() => setSelectedAddons({}), [
    setSelectedAddons,
  ]);

  const {
    selectAddonOptionValueById,
    selectIngredientById,
    addonOptions,
  } = useProductVariantAddonOptions(selectedVariant?.id);

  const { addonRules } = useProductVariantAddonRules(
    selectedVariant?.id,
  );

  const selectedAddonOptionValues = selectedAddonOptionValueIds
    .map((selectedAddonOptionValueId) =>
      selectAddonOptionValueById(selectedAddonOptionValueId),
    )
    .filter(Boolean);

  useEffect(() => {
    if (selectedVariant)
      analytics.viewProductVariant({
        product,
        productVariant: selectedVariant,
      });
  }, [product, selectedVariant]);

  useEffect(() => {
    const nextVisibleSelectedAddons = getNextVisibleSelectedAddons({
      addonOptions: addonOptions ?? [],
      addonRules,
      selectedAddons,
    });

    setIsAddonOptionsValid(
      shallowEqual(selectedAddons, nextVisibleSelectedAddons),
    );
  }, [addonOptions, addonRules, selectedAddons]);

  const isCustomizableProduct = useMemo(
    () => isCustomizableMeal(product),
    [product],
  );

  return (
    <div className={'product-details'}>
      <TextBalancer
        as="h3"
        className="product-title tw-font-[800] tw-text-[3.4rem] tw-mb-0"
      >
        {product.name}
      </TextBalancer>

      <ProductPrice
        variant={selectedVariant}
        addonOptionValues={selectedAddonOptionValues}
      />

      <div className="product-content">
        <p
          className={classNames(
            style['dont-break-out'],
            style['preserve-new-line'],
          )}
          dangerouslySetInnerHTML={{
            __html: formatMarkdownToHtml(product.short_description),
          }}
          style={{ lineHeight: 1.4 }}
        />
      </div>

      {productVariantsUtils.hasProductVariants(product) && (
        <ProductVariantPicker
          productVariants={product.variants}
          productOptions={product.options}
          value={{ variant: selectedVariant }}
          onChange={({ variant }) => {
            setSelectedVariant(variant);
          }}
        />
      )}

      <ProductVariantAddonSelectGroup
        productVariantId={selectedVariant?.id}
        value={selectedAddons}
        onChange={setSelectedAddons}
      />

      <div className="tw-flex tw-items-center tw-mb-6">
        <div className="details-filter-row details-row-size tw-mb-0 tw-w-full tw-justify-end">
          {/* <label htmlFor="qty">Qty:</label> */}
          <div className="tw-flex tw-items-center tw-gap-8">
            {/* <div className="product-details-quantity">
              <CartLineQuantityInput
                id="qty"
                name="quantity"
                productVariant={selectedVariant}
                value={quantity}
                onChange={setQuantity}
                data-decimals="0"
                required
              />
            </div> */}

            {isCustomizableProduct && (
              <div
                className="tw-text-[#666] tw-cursor-pointer tw-flex tw-items-center tw-gap-4 hover:tw-text-[#A42E27] tw-transition-colors tw-duration-200"
                onClick={resetMeal}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
                Reset My Meal
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="product-details-action tw-flex-row tw-flex-nowrap tw-gap-4">
        {/* <AddToCart
          product={product}
          productVariant={selectedVariant}
          quantity={quantity}
          addonOptionValueIds={selectedAddonOptionValueIds}
          onFirstItemAdded={onFirstItemAdded}
        >
          {({
            loading,
            isOutOfStock,
            canAddToCart,
            addToCart,
            isAddingToCart,
          }) => {
            const getLabel = () => {
              if (loading) return 'Loading...';
              if (!isAddonOptionsValid) return 'Plz reset meal';
              if (isOutOfStock || isAnyAddonOutOfStock)
                return 'We Sold Out!';
              return 'ADD TO CART';
            };

            const disabled =
              !canAddToCart ||
              isAnyAddonOutOfStock ||
              !isAddonOptionsValid;

            return (
              <Button
                style={
                  isIEBrowser() || isEdgeBrowser()
                    ? { minHeight: '4rem' }
                    : {}
                }
                disabled={disabled}
                variant="outlined"
                color={'primary'}
                loading={isAddingToCart}
                onClick={addToCart}
                className={`btn-product btn-cart tw-min-w-0 tw-px-4 md:tw-px-6 ${
                  !loading && !isAddonOptionsValid
                    ? 'invalid-addon'
                    : ''
                }`}
              >
                {!isAddonOptionsValid && !loading ? (
                  <em className="fa fa-exclamation-triangle tw-text-lg tw-mr-2" />
                ) : null}
                <span>{getLabel()}</span>
              </Button>
            );
          }}
        </AddToCart> */}
        <div className="tw-mx-0">
          <SeeMenu
            catalogueIds={catalogueIds}
            categories={categories}
            collections={collections}
            specialProductCategories={specialProductCategories}
          />
        </div>
      </div>

      <ProductShare
        product={product}
        selectedVariant={selectedVariant}
        selectedAddonOptionValues={selectedAddonOptionValues}
        selectIngredientById={selectIngredientById}
      />

      <div className="product-details-footer">
        <div>
          <Links
            label="Category"
            data={product?.categories?.map((cat) => ({
              to: `${routePaths.PRODUCTS}?category_ids=${cat.id}`,
              label: cat.name,
            }))}
          />

          <div className="mt-1">
            <Links
              label="Tag"
              data={product?.tags?.map((tag) => ({
                to: `${routePaths.PRODUCTS}?tag_ids=${tag.id}`,
                label: tag.name,
              }))}
            />
          </div>
        </div>
      </div>

      {children}
    </div>
  );
};

ProductDetail.propTypes = {
  product: typedef.Product,
  isWishlist: PropTypes.bool,
  addToCart: PropTypes.func,
  toggleWishlist: PropTypes.func,
  catalogueIds: PropTypes.string,
  onFirstItemAdded: PropTypes.func,
};

ProductDetail.defaultProps = {
  product: {},
  isWishlist: false,
  addToCart: () => {},
  toggleWishlist: () => {},
};

export default ProductDetail;
